import { useAccount, useMsal } from '@azure/msal-react'
import { useMenu } from '@if/mui'
import { Button, Divider, Menu, MenuItem } from '@mui/material'
import { useOnboarding, type TutorialKey } from '@src/providers/OnboardingProvider'
import { useUser } from '@src/providers/UserProvider'
import { CLEAR_CACHE_EXCEPTION_KEY } from '@src/types/constants'
import type { DocsAutomation } from '@src/types/automationDocument'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const clearCache = () => {
  const persistentValues: Record<string, string> = {}
  CLEAR_CACHE_EXCEPTION_KEY.forEach((key) => {
    const cachedValue = localStorage.getItem(key)
    if (cachedValue) persistentValues[key] = cachedValue
  }, {})
  localStorage.clear()
  Object.entries(persistentValues).forEach(([key, value]) => {
    localStorage.setItem(key, value)
  })
}

const ProfileMenu = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { instance } = useMsal()
  const account = useAccount()
  const [buttonProps, menuProps, handleMenuClose] = useMenu()
  const user = useUser()
  const { startTour, openHelp } = useOnboarding()
  const queryClient = useQueryClient()

  const handleLogout = async () => {
    handleMenuClose()
    clearCache()
    if (account) await instance.logoutRedirect({ account })
    else void navigate('/')
  }

  const handleStartTour = (key: TutorialKey) => {
    handleMenuClose()
    const currentPath = window.location.pathname
    const onDetailPage = /^\/documents\/[^/]+/.exec(currentPath) !== null
    const onListPage = !onDetailPage && (currentPath === '/' || currentPath.startsWith('/documents'))

    if (key === 'list') {
      if (!onListPage) {
        const savedUrl = sessionStorage.getItem('documentsListUrl') || '/documents'
        void navigate(savedUrl)
      }
    } else {
      // detail and finalize tutorials need a matched doc on the detail page
      const firstDocId = findFirstMatchedDocId(queryClient)
      if (firstDocId) void navigate(`/documents/${firstDocId}`)
    }

    startTour(key)
  }

  const handleOpenHelp = () => {
    handleMenuClose()
    openHelp()
  }

  return (
    <>
      <Button {...buttonProps}>{user?.name}</Button>
      <Menu {...menuProps}>
        <MenuItem onClick={() => handleStartTour('list')}>
          {t('tour.tutorials.list.menuLabel')}
        </MenuItem>
        <MenuItem onClick={() => handleStartTour('detail')}>
          {t('tour.tutorials.detail.menuLabel')}
        </MenuItem>
        <MenuItem onClick={() => handleStartTour('finalize')}>
          {t('tour.tutorials.finalize.menuLabel')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenHelp}>{t('tour.help.menuLabel')}</MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          {t('menus.logout')}
        </MenuItem>
      </Menu>
    </>
  )
}

const findFirstMatchedDocId = (queryClient: ReturnType<typeof useQueryClient>): string | null => {
  const queries = queryClient.getQueriesData<DocsAutomation[]>({
    predicate: (query) => {
      const [key] = query.queryKey as [string]
      return key === 'docsAutomation'
    },
  })
  for (const [, data] of queries) {
    if (!data) continue
    const matched = data.find((d) => d.linkedCreditApp)
    if (matched) return matched.id
  }
  for (const [, data] of queries) {
    if (data && data.length > 0) return data[0].id
  }
  return null
}

export default ProfileMenu
